import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import { Pagination } from "../../../components";
import { formatMoney } from "../../../utils";
import {
  useGetAllCooperativesQuery,
  useGetCooperativesQuery,
} from "../../../store/auth/authApi";
import {
  useGetPlatformLedgerQuery,
  useGetBranchWithdrawalsQueueQuery,
  useApproveBranchWithdrawalMutation,
  useFinalizeBranchWithdrawalTransferMutation,
  useRejectBranchWithdrawalMutation,
  useGetBranchWalletsForCompanyQuery,
} from "../../../store/settlementApi";
import PlatformLedgerDetailModal from "./PlatformLedgerDetailModal";
import {
  settlementTableWrap,
  settlementTh,
  settlementThRight,
  settlementTd,
  settlementTdMono,
  settlementTdRight,
} from "./settlementTableStyles";

const TABS = [
  { id: "ledger", label: "Platform ledger" },
  { id: "withdrawals", label: "Branch withdrawals" },
  { id: "wallets", label: "Branch wallets" },
];

const CATEGORY_OPTIONS = [
  { value: "", label: "All categories" },
  { value: "CONVENIENCE_REVENUE", label: "Convenience revenue" },
  { value: "PAYSTACK_COLLECTION_FEE_COST", label: "Paystack collection cost" },
  { value: "PAYOUT_TRANSFER_COST", label: "Payout transfer cost" },
];

const DIRECTION_OPTIONS = [
  { value: "", label: "All" },
  { value: "credit", label: "Credit (money in)" },
  { value: "debit", label: "Debit (money out)" },
];

const WITHDRAWAL_STATUS = [
  { value: "", label: "All statuses" },
  { value: "PENDING", label: "Pending" },
  { value: "PROCESSING", label: "Processing" },
  { value: "PAID", label: "Paid" },
  { value: "FAILED", label: "Failed" },
  { value: "REJECTED", label: "Rejected" },
];

function formatDateTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return "—";
  }
}

function formatLedgerCategoryCell(cat) {
  if (!cat) return "—";
  return String(cat)
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function TableSkeleton({ cols = 8 }) {
  return (
    <div className="overflow-x-auto border border-gray-200 rounded-lg">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            {Array.from({ length: cols }).map((_, i) => (
              <th key={i} className="px-4 py-3">
                <div className="h-3 bg-gray-200 rounded animate-pulse w-24" />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: 8 }).map((_, r) => (
            <tr key={r}>
              {Array.from({ length: cols }).map((_, c) => (
                <td key={c} className="px-4 py-3">
                  <div className="h-4 bg-gray-100 rounded animate-pulse w-full max-w-[120px]" />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function SettlementPage() {
  const [tab, setTab] = useState("ledger");

  const [ledgerPage, setLedgerPage] = useState(1);
  const [companyId, setCompanyId] = useState("");
  const [branchId, setBranchId] = useState("");
  const [direction, setDirection] = useState("");
  const [category, setCategory] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [wdPage, setWdPage] = useState(1);
  const [wdStatus, setWdStatus] = useState("");

  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectId, setRejectId] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [approveOpen, setApproveOpen] = useState(false);
  const [approveId, setApproveId] = useState(null);
  const [otpModalOpen, setOtpModalOpen] = useState(false);
  const [otpPendingId, setOtpPendingId] = useState(null);
  const [otpInput, setOtpInput] = useState("");
  const [ledgerDetailRow, setLedgerDetailRow] = useState(null);

  const { data: cooperativesRaw } = useGetAllCooperativesQuery();
  const cooperatives = useMemo(() => {
    const d = cooperativesRaw?.data;
    if (Array.isArray(d)) return d;
    if (Array.isArray(d?.data)) return d.data;
    return [];
  }, [cooperativesRaw]);

  const { data: coopDetail } = useGetCooperativesQuery(companyId, {
    skip: !companyId,
  });

  const branches = useMemo(() => {
    const b = coopDetail?.data?.branches;
    return Array.isArray(b) ? b : [];
  }, [coopDetail]);

  const companyNameById = useMemo(() => {
    const m = {};
    cooperatives.forEach((c) => {
      const id = c.company_id ?? c.id;
      if (id != null) m[id] = c.name || `Company #${id}`;
    });
    return m;
  }, [cooperatives]);

  const branchNameById = useMemo(() => {
    const m = {};
    branches.forEach((b) => {
      if (b?.id != null) {
        m[b.id] = b.name || b.branch_name || `Branch #${b.id}`;
      }
    });
    return m;
  }, [branches]);

  const ledgerCompanyLabel = (row) => companyNameById[row.company_id] || null;
  const ledgerBranchLabel = (row) =>
    row.branch_id != null ? branchNameById[row.branch_id] || `Branch #${row.branch_id}` : "—";

  /** Branch name when cooperative filter matches the row; else numeric id label */
  const withdrawalBranchLabel = (row) => {
    if (row.branch_id == null) return "—";
    if (companyId && String(row.company_id) === String(companyId)) {
      return branchNameById[row.branch_id] || `Branch #${row.branch_id}`;
    }
    return `Branch #${row.branch_id}`;
  };

  const ledgerParams = {
    page: ledgerPage,
    per_page: 20,
    ...(companyId && { company_id: companyId }),
    ...(branchId && { branch_id: branchId }),
    ...(direction && { direction }),
    ...(category && { category }),
    ...(fromDate && { from_date: fromDate }),
    ...(toDate && { to_date: toDate }),
  };

  const {
    data: ledgerResult,
    isFetching: ledgerLoading,
    error: ledgerError,
  } = useGetPlatformLedgerQuery(ledgerParams, { skip: tab !== "ledger" });

  const wdParams = {
    page: wdPage,
    per_page: 20,
    ...(companyId && { company_id: companyId }),
    ...(branchId && { branch_id: branchId }),
    ...(wdStatus && { status: wdStatus }),
  };

  const {
    data: wdResult,
    isFetching: wdLoading,
    error: wdError,
  } = useGetBranchWithdrawalsQueueQuery(wdParams, { skip: tab !== "withdrawals" });

  const {
    data: walletRows = [],
    isFetching: walletsLoading,
  } = useGetBranchWalletsForCompanyQuery(
    { companyId, branch_id: branchId || undefined },
    { skip: tab !== "wallets" || !companyId }
  );

  const [approveWd, { isLoading: approving }] = useApproveBranchWithdrawalMutation();
  const [finalizeWd, { isLoading: finalizingOtp }] = useFinalizeBranchWithdrawalTransferMutation();
  const [rejectWd, { isLoading: rejecting }] = useRejectBranchWithdrawalMutation();

  const ledgerRows = ledgerResult?.rows ?? [];
  const ledgerMeta = ledgerResult?.meta ?? {};

  const wdRows = wdResult?.rows ?? [];
  const wdMeta = wdResult?.meta ?? {};

  const handleCompanyChange = (e) => {
    const v = e.target.value;
    setCompanyId(v);
    setBranchId("");
    setLedgerPage(1);
    setWdPage(1);
  };

  const applyLedgerFilters = () => setLedgerPage(1);
  const clearLedgerFilters = () => {
    setCompanyId("");
    setBranchId("");
    setDirection("");
    setCategory("");
    setFromDate("");
    setToDate("");
    setLedgerPage(1);
  };

  const openApprove = (id) => {
    setApproveId(id);
    setApproveOpen(true);
  };

  const submitApprove = async () => {
    if (!approveId || approving) return;
    try {
      const res = await approveWd(approveId).unwrap();
      if (res?.status === false) {
        toast.error(res?.message || "Failed.");
        return;
      }
      const inner = res?.data ?? {};
      if (inner.requires_otp) {
        toast.success(res?.message || "Enter the verification code from your payment provider.");
        setApproveOpen(false);
        setOtpPendingId(approveId);
        setOtpInput("");
        setOtpModalOpen(true);
        setApproveId(null);
        return;
      }
      toast.success(res?.message || "Withdrawal processed.");
      setApproveOpen(false);
      setApproveId(null);
    } catch (err) {
      toast.error(err?.data?.message || err?.message || "Approve failed.");
    }
  };

  const submitTransferVerification = async () => {
    if (!otpPendingId) return;
    const otp = String(otpInput || "").trim();
    if (otp.length < 4) {
      toast.error("Enter the verification code (PIN) you received.");
      return;
    }
    try {
      const res = await finalizeWd({ id: otpPendingId, otp }).unwrap();
      if (res?.status === false) {
        toast.error(res?.message || "Verification failed.");
        return;
      }
      toast.success(res?.message || "Transfer verified.");
      setOtpModalOpen(false);
      setOtpPendingId(null);
      setOtpInput("");
    } catch (err) {
      toast.error(err?.data?.message || err?.message || "Verification failed.");
    }
  };

  const openReject = (id) => {
    setRejectId(id);
    setRejectReason("");
    setRejectOpen(true);
  };

  const submitReject = async () => {
    if (!rejectId) return;
    try {
      const res = await rejectWd({ id: rejectId, reason: rejectReason || undefined }).unwrap();
      if (res?.status !== false) {
        toast.success(res?.message || "Withdrawal rejected.");
        setRejectOpen(false);
        setRejectId(null);
      } else {
        toast.error(res?.message || "Failed.");
      }
    } catch (err) {
      toast.error(err?.data?.message || err?.message || "Reject failed.");
    }
  };

  const totalPagesLedger = ledgerMeta.lastPage || 1;
  const totalPagesWd = wdMeta.lastPage || 1;

  return (
    <div className="min-h-screen p-6">
      <div className="bg-white rounded-xl p-6 mb-4 shadow-sm border border-gray-100">
        <h1 className="text-2xl font-bold text-[#45464e] mb-1">Settlement & fees</h1>
        <p className="text-gray-500 text-sm mb-4">
          Platform fee ledger (convenience vs Paystack costs) and cooperative branch withdrawal
          queue. Filter by organization and branch; branch list loads when a cooperative is selected.
        </p>

        <div className="flex flex-wrap gap-2 border-b border-gray-200 pb-3 mb-4">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                tab === t.id
                  ? "bg-[#8B313A] text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Shared org / branch filters */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Cooperative</label>
            <select
              value={companyId}
              onChange={handleCompanyChange}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-[#8B313A] focus:border-[#8B313A]"
            >
              <option value="">All cooperatives</option>
              {cooperatives.map((c) => (
                <option key={c.company_id || c.id} value={c.company_id || c.id}>
                  {c.name || `Company #${c.company_id || c.id}`}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Branch</label>
            <select
              value={branchId}
              onChange={(e) => {
                setBranchId(e.target.value);
                setLedgerPage(1);
                setWdPage(1);
              }}
              disabled={!companyId}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-[#8B313A] focus:border-[#8B313A] disabled:bg-gray-100"
            >
              <option value="">All branches</option>
              {branches.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name || b.branch_name || `Branch #${b.id}`}
                </option>
              ))}
            </select>
          </div>
        </div>

        {tab === "ledger" && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Direction</label>
                <select
                  value={direction}
                  onChange={(e) => setDirection(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                >
                  {DIRECTION_OPTIONS.map((o) => (
                    <option key={o.value || "all"} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                >
                  {CATEGORY_OPTIONS.map((o) => (
                    <option key={o.value || "all"} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">From date</label>
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">To date</label>
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                />
              </div>
              <div className="flex items-end gap-2 lg:col-span-2">
                <button
                  type="button"
                  onClick={applyLedgerFilters}
                  className="bg-[#8B313A] hover:bg-[#6B252A] text-white px-4 py-2 rounded-lg text-sm font-medium"
                >
                  Apply
                </button>
                <button
                  type="button"
                  onClick={clearLedgerFilters}
                  className="border border-gray-300 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50"
                >
                  Clear
                </button>
              </div>
            </div>

            {ledgerError && (
              <p className="text-red-600 text-sm mb-4">
                {ledgerError?.data?.message || ledgerError?.message || "Failed to load ledger."}
              </p>
            )}

            {ledgerLoading ? (
              <TableSkeleton cols={8} />
            ) : ledgerRows.length === 0 ? (
              <p className="text-gray-500 text-sm py-8 text-center">No ledger entries match your filters.</p>
            ) : (
              <div className={settlementTableWrap}>
                <table className="min-w-full w-full border-collapse">
                  <thead>
                    <tr>
                      <th className={settlementTh}>Date</th>
                      <th className={settlementTh}>Cooperative</th>
                      <th className={settlementTh}>Branch</th>
                      <th className={settlementTh}>Direction</th>
                      <th className={settlementTh}>Category</th>
                      <th className={settlementThRight}>Amount</th>
                      <th className={settlementTh}>Reference</th>
                      <th className={`${settlementTh} text-center w-[100px]`}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ledgerRows.map((row) => (
                      <tr key={row.id} className="hover:bg-[#f7f7f7] transition-colors">
                        <td className={`${settlementTd} whitespace-nowrap`}>{formatDateTime(row.created_at)}</td>
                        <td className={settlementTd}>
                          {row.company_id ? (
                            <Link
                              to={`/cooperatives/${row.company_id}`}
                              className="text-[#8B313A] font-medium hover:underline"
                            >
                              {ledgerCompanyLabel(row) || `Company #${row.company_id}`}
                            </Link>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td className={settlementTd}>{ledgerBranchLabel(row)}</td>
                        <td className={settlementTd}>
                          <span
                            className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold text-white ${
                              String(row.direction).toLowerCase() === "credit" ? "bg-[#19bc31]" : "bg-[#ff4343]"
                            }`}
                          >
                            {String(row.direction || "—").toUpperCase()}
                          </span>
                        </td>
                        <td className={`${settlementTd} max-w-[220px]`}>
                          <span className="break-words leading-snug">{formatLedgerCategoryCell(row.category)}</span>
                        </td>
                        <td className={settlementTdRight}>
                          {formatMoney(Number(row.amount_ngn || 0).toFixed(2))}
                        </td>
                        <td className={settlementTdMono}>
                          <span className="line-clamp-2" title={row.payment_reference || ""}>
                            {row.payment_reference || "—"}
                          </span>
                        </td>
                        <td className={`${settlementTd} text-center`}>
                          <button
                            type="button"
                            onClick={() => setLedgerDetailRow(row)}
                            className="text-sm font-medium text-white bg-[#31778c] hover:bg-[#255a6b] px-3 py-1.5 rounded-lg transition-colors"
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {ledgerRows.length > 0 && (
              <div className="mt-4 flex justify-center">
                <Pagination
                  currentPage={ledgerMeta.currentPage || 1}
                  totalPages={totalPagesLedger}
                  hasNextPage={ledgerMeta.hasMorePages}
                  hasPrevPage={(ledgerMeta.currentPage || 1) > 1}
                  onPageChange={setLedgerPage}
                />
              </div>
            )}
          </>
        )}

        {tab === "withdrawals" && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={wdStatus}
                  onChange={(e) => {
                    setWdStatus(e.target.value);
                    setWdPage(1);
                  }}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                >
                  {WITHDRAWAL_STATUS.map((o) => (
                    <option key={o.value || "all"} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {wdError && (
              <p className="text-red-600 text-sm mb-4">
                {wdError?.data?.message || wdError?.message || "Failed to load withdrawals."}
              </p>
            )}

            {wdLoading ? (
              <TableSkeleton cols={9} />
            ) : wdRows.length === 0 ? (
              <p className="text-gray-500 text-sm py-8 text-center">No withdrawal requests match your filters.</p>
            ) : (
              <div className={settlementTableWrap}>
                <table className="min-w-full w-full border-collapse">
                  <thead>
                    <tr>
                      <th className={settlementTh}>ID</th>
                      <th className={settlementTh}>Cooperative</th>
                      <th className={settlementTh}>Branch</th>
                      <th className={settlementThRight}>Amount</th>
                      <th className={settlementThRight}>Wallet balance</th>
                      <th className={settlementTh}>Status</th>
                      <th className={settlementTh}>Created</th>
                      <th className={`${settlementTh} text-center min-w-[140px]`}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {wdRows.map((row) => (
                      <tr key={row.id} className="hover:bg-[#f7f7f7] transition-colors">
                        <td className={settlementTd}>{row.id}</td>
                        <td className={settlementTd}>
                          {row.company_id ? (
                            <Link
                              to={`/cooperatives/${row.company_id}`}
                              className="text-[#8B313A] font-medium hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#8B313A]/30 rounded"
                            >
                              {companyNameById[row.company_id] || `#${row.company_id}`}
                            </Link>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td className={settlementTd}>{withdrawalBranchLabel(row)}</td>
                        <td className={settlementTdRight}>
                          {formatMoney(Number(row.amount_ngn || 0).toFixed(2))}
                        </td>
                        <td className={settlementTdRight}>
                          {row.branch_wallet_balance_ngn != null && row.branch_wallet_balance_ngn !== ""
                            ? formatMoney(Number(row.branch_wallet_balance_ngn).toFixed(2))
                            : "—"}
                        </td>
                        <td className={settlementTd}>
                          <span
                            className="inline-flex px-2 py-1 rounded-full text-xs font-medium text-white"
                            style={{
                              backgroundColor:
                                row.status === "PENDING"
                                  ? "#ff9500"
                                  : row.status === "PAID"
                                    ? "#19bc31"
                                    : row.status === "FAILED" || row.status === "REJECTED"
                                      ? "#ff4343"
                                      : "#31778c",
                            }}
                          >
                            {row.status}
                          </span>
                        </td>
                        <td className={`${settlementTd} whitespace-nowrap`}>{formatDateTime(row.created_at)}</td>
                        <td className={`${settlementTd} text-center`}>
                          {row.status === "PENDING" && (
                            <div className="flex flex-wrap gap-2 justify-center">
                              <button
                                type="button"
                                disabled={approving}
                                onClick={() => openApprove(row.id)}
                                className="text-sm text-white bg-[#31778c] px-3 py-1 rounded-lg hover:opacity-90 disabled:opacity-50"
                              >
                                Approve & pay
                              </button>
                              <button
                                type="button"
                                disabled={rejecting}
                                onClick={() => openReject(row.id)}
                                className="text-sm text-[#8B313A] border border-[#8B313A] px-3 py-1 rounded-lg hover:bg-red-50"
                              >
                                Reject
                              </button>
                            </div>
                          )}
                          {row.status === "PROCESSING" &&
                            String(row.paystack_transfer_id || "").startsWith("TRF_") && (
                              <button
                                type="button"
                                onClick={() => {
                                  setOtpPendingId(row.id);
                                  setOtpInput("");
                                  setOtpModalOpen(true);
                                }}
                                className="text-sm text-white bg-[#ff9500] px-3 py-1 rounded-lg hover:opacity-90"
                              >
                                Enter OTP
                              </button>
                            )}
                          {row.status === "FAILED" && row.failure_reason && (
                            <span className="text-xs text-red-600" title={row.failure_reason}>
                              {row.failure_reason.slice(0, 40)}
                              {row.failure_reason.length > 40 ? "…" : ""}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {wdRows.length > 0 && (
              <div className="mt-4 flex justify-center">
                <Pagination
                  currentPage={wdMeta.currentPage || 1}
                  totalPages={totalPagesWd}
                  hasNextPage={wdMeta.hasMorePages}
                  hasPrevPage={(wdMeta.currentPage || 1) > 1}
                  onPageChange={setWdPage}
                />
              </div>
            )}
          </>
        )}

        {tab === "wallets" && (
          <>
            {!companyId ? (
              <p className="text-gray-500 text-sm py-6">
                Select a <strong>cooperative</strong> above to view branch settlement wallet balances.
              </p>
            ) : walletsLoading ? (
              <TableSkeleton cols={4} />
            ) : !Array.isArray(walletRows) || walletRows.length === 0 ? (
              <p className="text-gray-500 text-sm py-6">No wallet rows for this filter.</p>
            ) : (
              <div className={settlementTableWrap}>
                <table className="min-w-full w-full border-collapse">
                  <thead>
                    <tr>
                      <th className={settlementTh}>Branch</th>
                      <th className={settlementThRight}>Balance (NGN)</th>
                      <th className={settlementThRight}>Pending recovery</th>
                    </tr>
                  </thead>
                  <tbody>
                    {walletRows.map((w) => (
                      <tr key={`${w.branch_id}-${w.id}`} className="hover:bg-[#f7f7f7] transition-colors">
                        <td className={settlementTd}>{w.branch_id}</td>
                        <td className={settlementTdRight}>
                          {formatMoney(Number(w.balance_ngn || 0).toFixed(2))}
                        </td>
                        <td className={settlementTdRight}>
                          {formatMoney(Number(w.pending_recovery_ngn || 0).toFixed(2))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>

      <PlatformLedgerDetailModal
        show={!!ledgerDetailRow}
        onHide={() => setLedgerDetailRow(null)}
        row={ledgerDetailRow}
        companyName={ledgerDetailRow ? ledgerCompanyLabel(ledgerDetailRow) : null}
        branchLabel={ledgerDetailRow ? ledgerBranchLabel(ledgerDetailRow) : null}
      />

      {approveOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div
            className="bg-white rounded-xl max-w-md w-full p-6 shadow-xl border border-gray-100"
            role="dialog"
            aria-modal="true"
            aria-labelledby="approve-withdrawal-title"
          >
            <h3 id="approve-withdrawal-title" className="text-lg font-semibold text-[#383838] mb-2">
              Approve withdrawal
            </h3>
            <p className="text-sm text-gray-600 mb-6 leading-relaxed">
              Send this payout to the cooperative&apos;s linked settlement bank account? The transfer is
              processed by the platform; ensure the amount and recipient are correct before you
              continue.
            </p>
            <div className="flex justify-end gap-2 flex-wrap">
              <button
                type="button"
                onClick={() => {
                  setApproveOpen(false);
                  setApproveId(null);
                }}
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 min-w-[100px]"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={approving}
                onClick={submitApprove}
                className="px-4 py-2 rounded-lg bg-[#31778c] text-white hover:bg-[#255a6b] disabled:opacity-50 min-w-[140px] font-medium"
              >
                {approving ? "Processing…" : "Approve & pay"}
              </button>
            </div>
          </div>
        </div>
      )}

      {otpModalOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4">
          <div
            className="bg-white rounded-xl max-w-md w-full p-6 shadow-xl border border-gray-100"
            role="dialog"
            aria-modal="true"
            aria-labelledby="transfer-verification-title"
          >
            <h3 id="transfer-verification-title" className="text-lg font-semibold text-[#383838] mb-2">
              Enter verification code
            </h3>
            <p className="text-sm text-gray-600 mb-4 leading-relaxed">
              Your payment provider sent a PIN by SMS or email (e.g. &quot;Transfers Verification PIN&quot;).
              Enter it below to authorize this payout. The code usually expires within a few minutes.
            </p>
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={12}
              value={otpInput}
              onChange={(e) => setOtpInput(e.target.value.replace(/\s/g, ""))}
              placeholder="e.g. 610656"
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm mb-4 tracking-widest font-mono"
            />
            <div className="flex justify-end gap-2 flex-wrap">
              <button
                type="button"
                onClick={() => {
                  setOtpModalOpen(false);
                  setOtpPendingId(null);
                  setOtpInput("");
                }}
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 min-w-[100px]"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={finalizingOtp}
                onClick={submitTransferVerification}
                className="px-4 py-2 rounded-lg bg-[#31778c] text-white hover:bg-[#255a6b] disabled:opacity-50 min-w-[140px] font-medium"
              >
                {finalizingOtp ? "Verifying…" : "Submit code"}
              </button>
            </div>
          </div>
        </div>
      )}

      {rejectOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Reject withdrawal</h3>
            <p className="text-sm text-gray-500 mb-3">Optional reason (shown in audit / logs).</p>
            <textarea
              className="w-full border border-gray-300 rounded-lg p-3 text-sm mb-4 min-h-[100px]"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Reason for rejection"
            />
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setRejectOpen(false);
                  setRejectId(null);
                }}
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={rejecting}
                onClick={submitReject}
                className="px-4 py-2 rounded-lg bg-[#8B313A] text-white hover:bg-[#6B252A] disabled:opacity-50"
              >
                {rejecting ? "Submitting…" : "Reject"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
